<template>
  <div class="chronology-comment">
    <!-- USER AVATAR -->
    <div class="chronology-comment__avatar">
      <avatar :person="commentInfo.creator" :size="avatarSize" />
    </div>

    <!-- DATE AND EDITED -->
    <div class="chronology-comment__date">
      {{ commentInfo.createdOn }}
      <span v-if="commentInfo.isEdited">&nbsp;&mdash;&nbsp;Edited</span>
    </div>

    <!-- ACTION -->
    <div class="chronology-comment__action-text" v-html="commentInfo.action" />

    <!-- VISIBILITY MESSAGE -->
    <div v-if="visibilityNote" class="chronology-comment__visibility-note">
      <el-tooltip>
        <template #content>
          <div>{{visibilityNote}}</div>
        </template>
        <i class="ddi ddi-not-visible" />
      </el-tooltip>
    </div>
    <div v-else-if="isInternalComment" class="chronology-comment__visibility-note">
      <el-tooltip>
        <template #content>
          <div>Hidden from Guest users</div>
        </template>
        <i class="ddi ddi-not-visible" />
      </el-tooltip>
    </div>

    <!-- EDIT FORM -->
    <el-form
      v-if="isEditing"
      :disabled="isFormLoading"
      :model="formData"
    >
      <el-form-item>
        <editor
          v-model="formData.content"
          :autofocus="true"
          :mention-users="commentInfo.mentionUsers"
          :existing-attachments="formData.fileList"
          :disabled="isFormLoading"
          :name="`comment-${commentInfo.id}`"
          allows-attachment
          allows-google-drive
          placeholder="Write your message..."
          @mentions-list="insertMentions"
          @files-list="addFileHandler"
          @file-list-removed="removeFileHandler"
        />
      </el-form-item>

      <div class="chronology-comment__form-actions">
        <dd-button
          :is-disabled="isFormLoading"
          type="text"
          color="grey"
          size="medium"
          @click="disableEditMode"
        >Cancel</dd-button>
        <dd-button
          :is-disabled="isFormLoading"
          :is-loading="isFormLoading"
          size="medium"
          color="blue"
          @click="submit"
        >Save Changes</dd-button>
      </div>
    </el-form>

    <!-- CONTENT -->
    <div
      v-else
      class="user-inputed-rich-text"
      v-html="commentInfo.description"
    />

    <!-- ATTACHMENTS LIST -->
    <attachments-list
      v-if="!isEditing"
      :attachments="attachmentsToDisplay"
      class="chronology-comment__attachments-list"
    />

    <!-- MORE OPTIONS -->
    <div v-if="!isEditing && (showMoreOptions || $screen.isMobile($mq)) && (!$permissions.isGuest || commentInfo.canEdit)" class="chronology-comment__more-options">
      <template v-if="$screen.isTabletOrDesktop($mq)">
        <!-- The condition goes on the tooltip: Element Plus warns when its trigger renders nothing -->
        <el-tooltip v-if="commentInfo.canEdit" placement="top">
          <i
            class="ddi ddi-edit"
            title="Edit your comment"
            @click="enableCommentEdit"
          />
          <template #content>
            <div>
              Edit your comment
            </div>
          </template>
        </el-tooltip>
        <el-tooltip v-if="!$permissions.isGuest" placement="top">
          <i
            class="ddi ddi-task-add"
            @click="initiateLinkedTask(commentInfo.id)"
          />
          <template #content>
            <div>
              Create linked task from this comment
            </div>
          </template>
        </el-tooltip>
      </template>
      <more-button-dropdown v-else vertical-ellipsis>
        <template #options-list>
          <ul>
            <li v-if="commentInfo.canEdit" @click="enableCommentEdit">
              <i class="ddi ddi-edit" />
              <span>Edit your comment</span>
            </li>
            <li v-if="!$permissions.isGuest" @click="initiateLinkedTask(commentInfo.id)">
              <i class="ddi ddi-task-add" />
              <span>Create linked task from this comment</span>
            </li>
          </ul>
        </template>
      </more-button-dropdown>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import {
  IChronologyCommentFormData,
  IChronologyComment,
} from '@/types/data-objects/item-histories'
import { IAttachment } from '@/types/data-objects/attachments'
import { useAppStore } from '@/modules/app/app.store'

export default defineComponent({
  name: 'ChronologyComment',

  props: {
    commentInfo: {
      type: Object as () => IChronologyComment,
      required: true,
    },
    visibilityNote: {
      type: String,
      default: '',
      required: false,
    },
    isInternalComment: {
      type: Boolean,
      default: false,
    },
    rolledOverComment: {
      type: Boolean,
      default: false,
    },
    // Function used when submit edited comment
    submitHandler: {
      type: Function,
      required: true,
      default: (): any => {},
    },
  },

  data() {
    return {
      isEditing: false,
      isFormLoading: false,
      originalFileList: [] as any[],
      formData: {
        content: '',
        fileList: [],
        listAtMentionIDs: [],
        listFileUploadIDs: [],
        listRemovedAttachmentIDs: [],
      } as IChronologyCommentFormData,
      showMoreOptions: false,
    }
  },

  computed: {
    attachmentsToDisplay(): any {
      return this.commentInfo.attachments.filter(
        item =>
          !item.hasOwnProperty('status') ||
          (item.hasOwnProperty('status') && item.status === 'success'),
      )
    },
    avatarSize(): number {
      return this.$screen.isDesktop(this.$mq) ? 40 : 24
    },
    hasAttachmentsToDisplay(): boolean {
      return this.attachmentsToDisplay.length > 0
    },
  },

  watch: {
    commentInfo: {
      immediate: true,
      handler(value) {
        this.formData.content = value.description
        this.formData.fileList = value.attachments
        this.originalFileList = value.attachments
      },
    },
    rolledOverComment: {
      handler(value) {
        if (this.$screen.isTabletOrDesktop(this.$mq)) {
          this.showMoreOptions = value
        }
      },
    },
  },

  mounted() {
    // Add comment data to form for further editing
    this.resetFormDataLists()
  },

  methods: {
    initiateLinkedTask(id: number) {
      // This vuex prop is being watched by the task/convo detail header
      useAppStore().itemEventForLinkedTaskID = id
    },
    enableCommentEdit() {
      this.isEditing = true
    },
    resetFormDataLists() {
      this.formData.listAtMentionIDs = []
      this.formData.listFileUploadIDs = []
      this.formData.listRemovedAttachmentIDs = []
      this.formData.fileList = this.commentInfo.attachments.filter(
        item =>
          !item.hasOwnProperty('status') ||
          (item.hasOwnProperty('status') && item.status === 'success'),
      )
    },

    disableEditMode() {
      this.isEditing = false
      this.formData.fileList = this.originalFileList
      this.resetFormDataLists()
    },

    /**
     * Insert mentions in attribute
     */
    insertMentions(matches: any) {
      this.formData.listAtMentionIDs = matches
    },

    /**
     * Insert file in FormData
     */
    addFileHandler(files: IAttachment[]) {
      files.forEach(file => {
        if (!this.formData.fileList.includes(file)) {
          this.formData.fileList.push(file)
        }
      })

      this.formData.listFileUploadIDs = this.formData.fileList
        .filter(item => item.status === 'ready')
        .map(item => item.id)
    },

    /**
     * Remove file from FormData
     */
    removeFileHandler(id: number) {
      const file = this.formData.fileList.find(item => item.id === id)

      this.formData.fileList = this.formData.fileList.filter(
        item => item.id !== id,
      )

      if (file.status && file.status === 'ready') {
        this.formData.listFileUploadIDs = this.formData.listFileUploadIDs.filter(
          item => item !== id,
        )
      } else {
        this.formData.listRemovedAttachmentIDs.push(id)
      }
    },

    async submit() {
      this.isFormLoading = true

      try {
        const data = {
          id: this.commentInfo.id,
          message: this.formData.content,
          listAtMentionIDs: this.formData.listAtMentionIDs,
          listFileUploadIDs: this.formData.listFileUploadIDs,
          listRemovedAttachmentIDs: this.formData.listRemovedAttachmentIDs,
        }

        await this.submitHandler(this.commentInfo.id, data)
      } catch (error) {
        this.$error.emit(error)
      } finally {
        this.isFormLoading = false
        this.disableEditMode()
      }
    },
  },
})
</script>

<style lang="scss">
.chronology-comment {
  position: relative;
  padding-left: 33px;

  @include media-breakpoint-up(lg) {
    padding-left: 58px;
  }

  &__avatar {
    position: absolute;
    top: 6px;
    left: 0;

    @include media-breakpoint-up(lg) {
      top: 0;
    }
  }

  &__date {
    @include lettering--p-sm;
    color: var(--color-text-primary);
  }

  &__action-text {
    p {
      margin-bottom: 20px;
      word-break: break-word;
      @include lettering--p-md;

      a {
        @include lettering--normal-link;
      }
    }
  }

  &__attachments-list {
    margin-top: $detail-description-bottom-buffer;
  }

  &__more-options {
    position: absolute;
    top: -14px;
    right: 0;

    @include media-breakpoint-up(md) {
      // To handle being over collapsed history region
      z-index: 4;
      display: flex;
      align-items: center;
      top: -42px;
      right: 0;
      border: $border--solid-light;
      border-radius: 8px;
      padding: 4px 10px;
      background-color: var(--color-background-primary);
      @include standard-box-shadow;

      .ddi {
        cursor: pointer;
        color: var(--color-text-third);
        padding: 4px 0;
      }

      .ddi-edit {
        padding-right: 10px;
        border-right: $border--solid-light;
        margin-right: 10px;
      }
    }
  }

  &__visibility-note {
    position: absolute;
    @include lettering--p-sm;
    color: var(--color-text-third);
    top: -12px;
    right: 0;

    i {
      margin-right: 4px;
      font-size: 16px;
      line-height: 0;
    }

    @include media-breakpoint-down(sm) {
      display: none;
    }
  }

  &__form-actions {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 10px;
  }
}
</style>
